import { create } from "zustand";
import { proxyAction } from "@/lib/auth/proxyAction";
import { logoutAction } from "@/app/actions";

export interface UserProfile {
  id: string;
  code: string;

  email: string;
  phoneNumber: string;
  username: string;

  firstname: string;
  lastname: string;
  image: string | null;

  isActive: boolean;
  passwordResetRequired: boolean;

  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;

  passwordResetExpires: string | null;
  passwordResetToken: string | null;

  notificationPreference: string | null;

  region: string;
  zone: string;
  woreda: string;
  kebele: string;

  languageCode: string;
  teamId: string;
  teamName: string;

  language: {
    id: string;
    code: string;
    name: string;
  };

  roles: string[];
  permissions: string[];
}

interface UserState {
  user: UserProfile | null;
  role: string | null;
  loading: boolean;
  hydrated: boolean;
  error?: string;

  setUser: (u: unknown | null) => void;
  fetchUserProfile: (options?: { force?: boolean }) => Promise<UserProfile | null>;
  logout: () => Promise<void>;
}

const isRecord = (value: unknown): value is Record<string, any> =>
  typeof value === "object" && value !== null;

const pickCodeLikeValue = (value: unknown): string | null => {
  if (typeof value === "string") {
    const cleaned = value.trim();
    return cleaned || null;
  }

  if (!isRecord(value)) return null;

  if (typeof value.code === "string" && value.code.trim()) return value.code.trim();
  if (typeof value.name === "string" && value.name.trim()) return value.name.trim();

  if (isRecord(value.role)) {
    if (typeof value.role.code === "string" && value.role.code.trim()) {
      return value.role.code.trim();
    }
    if (typeof value.role.name === "string" && value.role.name.trim()) {
      return value.role.name.trim();
    }
  }

  if (isRecord(value.permission)) {
    if (typeof value.permission.code === "string" && value.permission.code.trim()) {
      return value.permission.code.trim();
    }
    if (typeof value.permission.name === "string" && value.permission.name.trim()) {
      return value.permission.name.trim();
    }
  }

  return null;
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  const seen = new Set<string>();
  const output: string[] = [];

  for (const item of value) {
    const normalized = pickCodeLikeValue(item);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    output.push(normalized);
  }

  return output;
};

const normalizeUserProfile = (raw: unknown): UserProfile | null => {
  if (!isRecord(raw)) return null;

  const roles = toStringArray(raw.roles);
  const permissions = toStringArray(raw.permissions);

  return {
    ...(raw as UserProfile),
    roles,
    permissions,
  };
};

let profileRequestInFlight: Promise<UserProfile | null> | null = null;

export const useUserStore = create<UserState>((set, get) => ({
  user: null,
  role: null,
  loading: true,
  hydrated: false,
  error: undefined,

  setUser: (u) => {
    const normalized = u ? normalizeUserProfile(u) : null;

    set({
      user: normalized,
      role: normalized?.roles?.[0] ?? null,
      loading: false,
      hydrated: true,
      error: normalized ? undefined : u ? "Invalid profile response" : undefined,
    });
  },

  // userStore.ts
  fetchUserProfile: async (options) => {
    const force = options?.force ?? false;
    const state = get();

    if (!force) {
      if (state.hydrated && state.user) {
        return state.user;
      }

      if (state.hydrated && !state.user && state.error) {
        return null;
      }

      if (profileRequestInFlight) {
        return profileRequestInFlight;
      }
    }

    const run = async () => {
      set({ loading: true, error: undefined });

      const loadProfile = () =>
        proxyAction<unknown>("/profile/me", {
          method: "GET",
          auth: true,
        });

      let result = await loadProfile();

      // Right after login, cookie propagation can race the first profile read.
      if (!result.success && result.status === 401 && !get().user) {
        await new Promise((resolve) => setTimeout(resolve, 120));
        result = await loadProfile();
      }

      if (!result.success) {
        set({
          user: null,
          role: null,
          loading: false,
          hydrated: true,
          error: result.error,
        });
        return null;
      }

      const data = normalizeUserProfile(result.data);
      if (!data) {
        set({
          user: null,
          role: null,
          loading: false,
          hydrated: true,
          error: "Invalid profile response",
        });
        return null;
      }

      set({
        user: data,
        role: data?.roles?.[0] ?? null,
        loading: false,
        hydrated: true,
        error: undefined,
      });
      return data;
    };

    profileRequestInFlight = run().finally(() => {
      profileRequestInFlight = null;
    });

    return profileRequestInFlight;
  },
  logout: async () => {
    profileRequestInFlight = null;
    set({
      user: null,
      role: null,
      hydrated: false,
      loading: false,
    });

    await logoutAction();
  },
}));
